<template>
  <OrModalV3
    ref="modalComponent"
    size="m"
  >
    <template #header>
      {{ $t('contacts.mergeModal.title') }}
    </template>
    <div
      class="merge-content"
      data-test="merge-content"
    >
      <div class="contact-row radio-row">
        <div
          v-for="contact of contacts"
          :key="contact.contactId"
          class="contact-cell radio-cell"
          data-test="main-contact-radio-cell"
          :class="{ selected: contact.contactId === selectedId }"
          @click="selectContact(contact.contactId)"
        >
          <or-radio
            v-model="selected"
            :value="contact.contactId"
            :label="$t('contacts.mergeModal.mainContact')"
          />
        </div>
      </div>
      <div class="contact-info">
        <div class="contact-row">
          <div
            v-for="contact of contacts"
            :key="contact.contactId"
            class="contact-cell"
            :class="{ selected: contact.contactId === selectedId }"
            @click="selectContact(contact.contactId)"
          >
            <contacts-view-modal-profile-info
              :first-name="contact.firstName"
              :last-name="contact.lastName"
              :profile-image="contact.profileImage"
            />
          </div>
        </div>
        <div
          v-for="field of orderedSchemaItems"
          :key="field.order"
          class="contact-row"
        >
          <div
            v-for="contact of contacts"
            :key="contact.contactId"
            class="contact-cell"
            :class="{
              selected: contact.contactId === selectedId,
              deleting: !unionTypes.includes(field.typeOf)
            }"
            @click="selectContact(contact.contactId)"
          >
            <contacts-view-field
              :field-schema="field"
              :data="contact[field.key]"
              :always-show="propertiesToShow[field.key]"
            />
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="actions">
        <or-button
          type="outline"
          :content="$t('common.cancel')"
          data-test="cancel-merge-contacts-button"
          @click="$emit('close')"
        />
        <or-button
          :is-loading="isMerging"
          :content="$t('contacts.mergeModal.merge')"
          data-test="merge-contacts-button"
          @click="$emit('merge', selectedId, unselectedId)"
        />
      </div>
    </template>
  </OrModalV3>
</template>
<script lang="ts">
import { OrModalV3 } from '@onereach/ui-components';
import { OrButtonV2 as OrButton, OrRadio } from '@onereach/ui-components-legacy';
import { defineComponent, PropType, ref, computed } from 'vue';

import { CompositeColumnKeys, SchemaItemTypes } from '@/constants';
import { Contact, ContactPropertiesToShow, Schema, SchemaItemExtended } from '@/types';
import { getCompositeColumnsMapping, getContactPropertiesToShow, convertSchemaToOrderedArray } from '@/utils';

import { ContactsViewField, ContactsViewModalProfileInfo } from '../ContactsView/ContactsViewField';

const alreadyShownKeys = getCompositeColumnsMapping()[CompositeColumnKeys.NAME].parts;

const unionTypes: SchemaItemTypes[] = [
  SchemaItemTypes.ARRAY_OF_OBJECTS,
  SchemaItemTypes.ARRAY_OF_STRINGS,
  SchemaItemTypes.ARRAY_OF_IDENTIFIERS,
];

export default defineComponent({
  name: 'ContactMergeModal',
  components: {
    OrModalV3,
    OrButton,
    OrRadio,
    ContactsViewModalProfileInfo,
    ContactsViewField,
  },
  props: {
    isMerging: {
      type: Boolean,
      required: false,
      default: false,
    },
    schema: {
      type: Object as PropType<Schema>,
      required: false,
      default: () => null,
    },
    contacts: {
      type: Array as PropType<Contact[]>,
      required: false,
      default: () => [],
    },
  },
  emits: ['merge', 'close'],
  expose: ['modalComponent'],
  setup(props) {
    let mainContact = ref('');
    const selected = computed(() => {
      return (mainContact.value === '') ? props.contacts[0]?.contactId : mainContact.value;
    });
    return {
      modalComponent: ref<InstanceType<typeof OrModalV3>>(),
      unionTypes,
      mainContact,
      selected,
    };
  },

  computed: {
    selectedId(): string {
      return (this.selected as unknown as ({ value: string; }))?.value ?? this.selected;
    },
    unselectedId(): string {
      return this.contacts[0]?.contactId === this.selectedId ?
        this.contacts[1]?.contactId :
        this.contacts[0]?.contactId;
    },
    orderedSchemaItems(): SchemaItemExtended[] {
      return convertSchemaToOrderedArray(this.schema)
        .filter(({ key }) => !alreadyShownKeys.includes(key) && this.propertiesToShow[key]);
    },
    propertiesToShow(): ContactPropertiesToShow {
      return getContactPropertiesToShow(this.contacts);
    },
  },
  methods: {
    selectContact(contactId: string): void {
      this.mainContact = contactId;
    },
  },
});
</script>
<style lang="scss" scoped>
@use "@onereach/styles/src/global" as *;

.merge-content {
  .contact-row {
    display: flex;
    flex-direction: row;
    gap: $s-4;
    justify-content: space-around !important;
    background-color: $c-neutral-0;

    & > .contact-cell {
      flex: 1 1 0;
      min-width: 0;
      padding: $s-4 $s-4 0;
      background-color: $c-neutral-1;
      border: 1px solid $c-neutral-1-darken-5;
      border-top-width: 0;
      border-bottom-width: 0;

      &.selected {
        background-color: $c-primary-lighten-45;
        border-color: $c-primary;
      }

      &:not(.selected) {
        cursor: pointer;
      }

      &:not(.selected).deleting :deep(.text) {
        text-decoration: line-through;
      }

      & > * {
        height: 100%;
      }
    }

    & > .radio-cell {
      border-top-width: 1px;
      border-radius: $s-2 $s-2 0 0;
    }

    &:last-child > .contact-cell {
      border-bottom-width: 1px;
      border-radius: 0 0 $s-2 $s-2;
    }
  }

  .contact-info > .contact-row {
    &:first-child > .contact-cell {
      padding-top: $s-5;
    }

    &:not(:last-child, :first-child) > .contact-cell > * {
      border-bottom: 1px solid $c-neutral-2;
    }
  }
}

.contacts-view {
  display: flex;
  flex-direction: column;
  gap: $s-4;
}

.actions {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
}
</style>
